/**
 * Launcher-side Logger for circuit-chat-embed.
 *
 * Thin binding of the shared `createBrowserLogger` factory. Kept in a
 * dedicated launcher module so the launcher IIFE tree-shakes cleanly
 * and doesn't drag the SPA's clientLog transport into its bundle.
 *
 * Same wire shape as the SPA path (see `src/lib/logging/clientLog.ts`)
 * with `logger: 'launcher'` on every entry.
 */

import type { BrowserLogEntry } from '@circuit/circuit-utils/logging';

import { generateSafeUUID } from '@circuit/circuit-utils/id';
import {
  createBrowserLogger,
  stripUrlQueryString,
} from '@circuit/circuit-utils/logging';

import { CHAT_BOT_ROUTES, httpClient } from '@/lib/api';

const LOGGER_TAG_LAUNCHER = 'launcher';
const VERSION = import.meta.env.CIRCUIT_CHAT_EMBED_VERSION ?? 'dev';

const SESSION_STORAGE_KEY = 'circuit-chat-embed.session-id';
let cachedSessionId: string | null = null;

function getSessionId(): string {
  if (cachedSessionId !== null) return cachedSessionId;
  try {
    const existing = globalThis.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing !== null && existing !== '') {
      cachedSessionId = existing;
      return existing;
    }
    const fresh = generateSafeUUID();
    globalThis.sessionStorage.setItem(SESSION_STORAGE_KEY, fresh);
    cachedSessionId = fresh;
    return fresh;
  } catch {
    cachedSessionId ??= generateSafeUUID();
    return cachedSessionId;
  }
}

let boundApiKey = '';

export function bindLauncherLoggerApiKey(apiKey: string): void {
  boundApiKey = apiKey;
}

function transport(entry: BrowserLogEntry): void {
  const apiKey = boundApiKey;
  if (!apiKey) return;
  void httpClient({
    apiKey,
    body: JSON.stringify(entry),
    keepalive: true,
    method: 'POST',
    path: CHAT_BOT_ROUTES.clientLog,
    requestId: entry.metadata['X-Request-Id'],
  }).catch(() => undefined);
}

export const launcherLogger = createBrowserLogger(transport, {
  logger: LOGGER_TAG_LAUNCHER,
  sessionId: getSessionId,
  version: VERSION,
  getBrowserMeta: () => ({
    url: stripUrlQueryString(globalThis.location.href),
    userAgent: globalThis.navigator.userAgent,
  }),
});
